import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import UserRoles from "../../model/enums/UserRole";
import ConditionReport from "../../model/ConditionReport";
import ConditionReportsListElement from "./ConditionReportsListElement";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

// Define props interface for type safety
interface ConditionReportsListProps {
  name?: string;
  eid?: string;
  chelementid?: string;
  onItemClick?: (cid: number) => void;
  itemSelectionMode: boolean;
}

/*
  Component to manage the display of the existing SITES list
*/
function ConditionReportsList({
  name,
  eid,
  chelementid,
  onItemClick,
  itemSelectionMode,
}: ConditionReportsListProps) {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<ConditionReport[]>([]);
  // manage data loading animation
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // load record data from the server
  useEffect(() => {
    // Make the list usable as a component outside the condition report create route, which requires eid as route parameter.
    // Usa case: for selecting Condition Report to copy for the creation of a new one
    let used_e3d_id = id;
    if (eid) used_e3d_id = eid;

    let url = APIEndPoints.CONDITION_REPORTS.ALL(parseInt(used_e3d_id!));
    if (itemSelectionMode)
      url = APIEndPoints.CONDITION_REPORTS.OFCHELEMENT(parseInt(chelementid!));

    apiClient
      ?.get<PagedData<ConditionReport[]>>({
        url: url,
        requiresToken: true,
      })
      .then((response: PagedData<ConditionReport[]>) => {
        let filteredData = response.items;
        if (name) {
          filteredData = filteredData.filter((item) =>
            item.name.toLowerCase().includes(name.toLowerCase()),
          );
        }
        setData(filteredData.slice(0, 4));
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    apiClient,
    id,
    name,
    selectedId,
    navigate,
    chelementid,
    eid,
    itemSelectionMode,
  ]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="grid grid-cols-4 gap-6">
      {data?.length > 0 &&
        data?.map((item) => {
          const canEdit =
            !EmbeddingToolInfo.isChemiTool() &&
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN);
          return (
            <div
              key={item.id}
              // onClick={() => {
              //   if (item.id !== undefined) {
              //     setSelectedId(item.id);
              //     onItemClick?.(item.id);
              //   }
              // }}
              // className={` ${
              //   selectedId === item.id ? 'bg-green-200' : 'bg-white'
              // }`}
            >
              <ConditionReportsListElement
                data={item}
                key={item.id}
                canEdit={canEdit}
                navigable={true}
                itemSelectionMode={itemSelectionMode}
                onClick={() => {
                  if (itemSelectionMode && item.id !== undefined) {
                    setSelectedId(item.id);
                    onItemClick?.(item.id);
                  }
                }}
                selected={selectedId === item.id}
              />
            </div>
          );
        })}

      {data?.length === 0 && (
        <div className="details-no-records">
          No Condition Reports available.
        </div>
      )}
    </div>
  );
}

export default ConditionReportsList;
